import React, { PropsWithChildren, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Container, CircularProgress, Box, Avatar, Typography, Alert, Link } from "@mui/material";
import MaintenanceModal from "../components/MaintenanceModal";
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import { isAxiosError } from "axios";
import { apiClient, apiClientHooks, useRegisterAuthTokenProvider } from "./InitApiClient";
import { queryClient } from "./InitReactQuery";
import { Helmet } from "react-helmet-async";
import ShowInvites from "../components/invites/ShowInvites";
import { useIdTokenClaims } from "../components/auth0/useIdTokenClaims";
import SingleClickWaitingButton from "../components/SingleClickWaitingButton";
import { schemas } from "../generated/api/client";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { usePostHog } from 'posthog-js/react';

export const useGetMyProfileWithJwt = () =>
  apiClientHooks.useGetMyProfile({
    queries: {
      jwt: true,
    }
  })

export const refetchProfileJwt = () =>
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyProfile') })

const RegisterAuthTokenProvider: React.FC = () => {
  const getMyProfile = useGetMyProfileWithJwt()

  useRegisterAuthTokenProvider(
    'profile',
    async () => {
      const jwt = getMyProfile.data?.jwt
      if (!jwt) throw new Error('Profile JWT not yet set')
      return jwt
    },
    [getMyProfile.data?.jwt],
  )

  return null
}

export const useProfileAssured = () => {
  const getMyProfile = useGetMyProfileWithJwt()
  if (!getMyProfile.data) throw new Error('Profile not yet initialised')
  return getMyProfile.data
}

export const useProfileJwt = () => useGetMyProfileWithJwt().data?.jwt

const useLogoutLink = () => {
  const auth0 = useAuth0()

  return async () => {
    await auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }
}

const CreateProfile: React.FC = () => {
  const {
    givenName,
    familyName,
  } = useIdTokenClaims()

  const { idToken } = useIdTokenClaims()

  const onCreateProfile = async () => {
    // Create a profile, using the given name and family name from the Auth0 token (if it exists)
    // Missing fields are defaulted to empty strings
    try {
      const response = await apiClient.createMyProfile({
        given_name: givenName ?? '',
        family_name: familyName ?? '',
      }, {
        headers: { 'x-auth0-id-token': idToken ?? '' },
      })
      console.log("[CreateProfile] Create Response: ", response)
    } catch (error) {
      // DBS-358: the profile exists after all (e.g. a second create racing the first): load it rather than stop here
      if (!isAxiosError(error) || error.response?.data?.error?.type !== 'errProfileExistsByIdentity') throw error
      console.warn('[CreateProfile] Profile already exists; loading it')
    }

    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyProfile') })
  }

  // Automatically create a profile
  useEffect(() => {
    if (idToken !== undefined) {
      console.debug('[CreateProfile] Creating a profile...')
      onCreateProfile()
    } else {
      console.debug('[CreateProfile] No ID Token yet...')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken])

  return null
}

const zUpdateProfileFormSchema = schemas.createMyProfile_Body
type UpdateProfileFormSchema = z.infer<typeof zUpdateProfileFormSchema>

const UpdateProfileIfNameMissing: React.FC = () => {
  // Build the Form
  const form = useForm<UpdateProfileFormSchema>({
    resolver: zodResolver(zUpdateProfileFormSchema),
    mode: 'onChange',
  })

  const onUpdateProfile = async () => {
    await apiClient.updateMyProfile({
      given_name: form.getValues('given_name'),
      family_name: form.getValues('family_name'),
    })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyProfile') })
  }

  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  return <>
    <Helmet>
      <title>Profile Name</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h3" marginBottom={3}>
          Update Profile
        </Typography>
        <Typography variant="body1">
          Please tell us your name:
        </Typography>
        <TextFieldElement {...textFieldElementCommonProps} label="Forename" name="given_name" />
        <TextFieldElement {...textFieldElementCommonProps} label="Surname" name="family_name" />
        <SingleClickWaitingButton
          onClick={onUpdateProfile}
          variant="contained"
          color="primary"
          sx={{ marginTop: 3 }}
          disabled={!form.formState.isValid}
        >
          Update Profile
        </SingleClickWaitingButton>
      </Box>
    </Container>
  </>
}

const UnauthorisedAccess: React.FC = () => {
  const {
    email,
    givenName,
  } = useIdTokenClaims()

  const onClickLogout = useLogoutLink()

  return <>
    <Helmet>
      <title>Unauthorised Access</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <PersonOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" marginBottom={3}>
          Hi {givenName}!
        </Typography>
        <Typography variant="body1" marginBottom={3}>
          This site is for <strong>{process.env.REACT_APP_NAME_STUB}</strong> customers only.
        </Typography>
        <ShowInvites
          noInvites={<>
            <Alert severity="error" sx={{ marginBottom: 3 }}>
              You do not currently have customer access.
              <br />
              <br />
              Please request a <strong>customer</strong> invite for: <strong>{email}</strong>.
            </Alert>
          </>}
        />
        <Typography variant="body1" marginBottom={1}>
          <Link onClick={onClickLogout}>logout</Link>
        </Typography>
      </Box>
    </Container>
  </>
}

const InitRequireProfile: React.FC<PropsWithChildren> = ({ children }) => {
  const getMyProfile = useGetMyProfileWithJwt()
  const posthog = usePostHog()

  useEffect(() => {
    if (getMyProfile.data) {
      console.debug('Profile', {
        email: getMyProfile.data.email,
        given_name: getMyProfile.data.given_name,
        family_name: getMyProfile.data.family_name,
      })
      // Tell PostHog to link the Auth0 user to the Profile ID and add the profile information
      // This should mean you can find the user in Posthog by their Auth0 user or Profile ID, and that we'll see their email and name
      posthog?.alias(getMyProfile.data.id)
      posthog?.identify(getMyProfile.data.id, {
        email: getMyProfile.data.email,
        name: `${getMyProfile.data.given_name} ${getMyProfile.data.family_name}`,
        profile_id: getMyProfile.data.id,
        uses_applicant_app: true,
      })
    }
  }, [getMyProfile.data, posthog])

  // DBS-358: no data and no error yet is still loading, not a missing profile
  if (getMyProfile.isInitialLoading || (!getMyProfile.isError && getMyProfile.data === undefined)) {
    // We don't want to flash the create profile page while we're loading state
    return <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="50vh"
    >
      <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
        Loading Profile ...
      </Typography>
      <CircularProgress />
    </Box>
  }

  const isProfileNotFound = (() => {
    // getMyProfile returns a 401 error with data of `{ error: { type: 'errProfileNotFound' } }` when the profile is not found, and needs creating
    if (!getMyProfile.isError) return false
    if (!isAxiosError(getMyProfile.error)) return false
    return getMyProfile.error.response?.data?.error?.type === 'errProfileNotFound'
  })()

  if (isProfileNotFound) {
    return <CreateProfile />
  }

  // DBS-358: any other failure (expired session, network, server error) is not a new user: say so instead of creating one
  if (getMyProfile.isError || getMyProfile.data === undefined) {
    console.error('[InitRequireProfile] Error loading profile:', getMyProfile.error)
    return <MaintenanceModal open={true} error="Unable to load profile." />
  }

  if (!getMyProfile.data.given_name || !getMyProfile.data.family_name) {
    return <>
      <RegisterAuthTokenProvider />
      <UpdateProfileIfNameMissing />
    </>
  }

  return <>
    <RegisterAuthTokenProvider />
    {getMyProfile.data.admin_customer_ids.length > 0
      ? <>{children}</>
      : <UnauthorisedAccess />
    }
  </>
}

export default InitRequireProfile
